import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import { NavBar, NAVBAR_HEIGHT } from "@app.automotus.io/components/NavBar";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { FullScreenLoader } from "@app.automotus.io/components/FullScreenLoader";
import { useDashboardNavLinks } from "../layout/useDashboardNavLinks";

declare global {
  interface Window {
    zE?: (...args: unknown[]) => void;
  }
}

/**
 * Full-screen Zendesk messaging chat rendered below the NavBar, in place of
 * the usual redirect-to-Help-Center behavior, for authenticated users.
 *
 * The messenger's panel is an iframe Zendesk injects directly under `<body>`
 * (outside this component's tree), so it can't be laid out with ordinary
 * React/CSS composition — the GlobalStyles override below targets its real
 * DOM (confirmed live: `iframe[name="Messaging window"]`) and only exists
 * while this component is mounted. The floating launcher bubble (`iframe#launcher`)
 * is hidden app-wide in App.tsx, not here.
 */
export const SupportChat: React.FC = () => {
  const theme = useTheme();
  const { userProfile, loading } = useUserProfile();
  const targets = useDashboardNavLinks();

  // Kicks off the Zendesk messenger unconditionally, regardless of whether the
  // app-level user profile has resolved yet — the chat itself doesn't depend on
  // `userProfile`, so it shouldn't be artificially delayed behind that query.
  // Only the NavBar (which needs `userProfile` for its target list) is gated below.
  //
  // The Zendesk snippet (public/index.html) loads via a plain `async` <script>, with no
  // synchronous stub/queue — `window.zE` may not exist yet at mount, so a single
  // check-and-open can silently no-op (AUT-5559). Poll instead, same pattern as
  // ZendeskWidgetVisibility; cleared on unmount, so it's bounded to this page's lifetime.
  React.useEffect(() => {
    const POLL_INTERVAL_MS = 200;

    const intervalId = window.setInterval(() => {
      if (typeof window.zE === "function") {
        window.zE("messenger", "open");
        window.clearInterval(intervalId);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      if (typeof window.zE === "function") {
        window.zE("messenger", "close");
      }
    };
  }, []);

  return (
    <>
      <CssBaseline />
      {/* Kept outside the loading guard below: the messenger may already be opening/rendering
       * (the zE effect above fires immediately, independent of `userProfile`), so this override
       * needs to apply from the very first paint, not just once the profile resolves. */}
      <GlobalStyles
        styles={{
          'iframe[name="Messaging window"]': {
            visibility: "visible !important",
            position: "fixed !important",
            zIndex: "1250 !important",
            top: `${NAVBAR_HEIGHT.xs}px !important`,
            // Centered and capped at the theme's `xl` breakpoint, matching the max-width of
            // the Transactions table (PayerDashboard's `<Container maxWidth="xl">`).
            left: "50% !important",
            transform: "translateX(-50%) !important",
            width: "100% !important",
            maxWidth: `${theme.breakpoints.values.xl}px !important`,
            bottom: "0 !important",
            height: `calc(100vh - ${NAVBAR_HEIGHT.xs}px) !important`,
            [theme.breakpoints.up("lg")]: {
              top: `${NAVBAR_HEIGHT.lg}px !important`,
              height: `calc(100vh - ${NAVBAR_HEIGHT.lg}px) !important`,
            },
          },
        }}
      />
      {loading ? (
        <FullScreenLoader loading />
      ) : (
        <>
          {/* The NavBar needs `userProfile` for its target list, so it's the only
           * part gated on the profile query. The Help Center link below is not:
           * it's this component's safety net for a chat that fails to load, and
           * gating it behind an unrelated query would mean a profile error (which
           * Apollo resolves terminally as `loading: false, userProfile: undefined`,
           * not a throw) renders nothing at all. */}
          {userProfile && (
            <NavBar targets={targets} supportLabel="Resources" supportUrl={process.env.REACT_APP_SUPPORT_URL || ""} />
          )}
          <Box
            component="main"
            sx={{
              pt: userProfile ? { xs: `${NAVBAR_HEIGHT.xs}px`, lg: `${NAVBAR_HEIGHT.lg}px` } : 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              p: 4,
            }}
          >
            <Typography variant="body1" textAlign="center">
              Having trouble loading chat?{" "}
              <Link href={process.env.REACT_APP_SUPPORT_URL || ""} target="_blank" rel="noopener noreferrer">
                Visit our Help Center
              </Link>
              .
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default SupportChat;
